"use client";

import { useLayoutEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import HoverArrow from "@/components/shared/HoverArrow";
import { EASE, fadeUp, stagger, viewportOnce } from "@/lib/motion";

/**
 * Ankündigungs-Banner für den Aufrück-Tracker: Brand-Gradient-Karte mit
 * „Jetzt Neu"-Badge, Copy links und zwei überlappenden, am unteren Rand
 * beschnittenen App-Mockups rechts. Wird innerhalb der FeaturesSection
 * direkt unter dem „Ein Werkzeug. Ein Ziel."-Header gerendert.
 */
export default function TrackerAnnouncementSection() {
  const prefersReduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  // Immer mit false starten (== SSR-Markup), sonst weicht der erste Client-
  // Render vom Server ab (Hydration-Mismatch). useLayoutEffect korrigiert den
  // Wert vor dem ersten Paint, damit Desktop trotzdem keinen Einflug sieht.
  const [isDesktop, setIsDesktop] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Mobile: Phones starten mittig gestapelt und gleiten beim Einscrollen
  // auseinander. Desktop: statischer Start, Hover skaliert und kippt sie leicht.
  const mobileEntrance = !isDesktop && !prefersReduced;
  const hoverActive = isDesktop && !prefersReduced && hovered;

  return (
    <motion.div
      id="aufrueck-tracker"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="card-flat relative overflow-hidden text-paper"
      style={{
        // Mehrfarbiger Mesh-Verlauf wie in der App-Referenz: Blau oben links,
        // Indigo in der Mitte, Cyan/Türkis unten rechts, Violett unten links.
        background: [
          // Kräftiges Cyan entlang der rechten Kante (Mitte bis unten)
          "radial-gradient(75% 130% at 108% 70%, #2fb7d3 0%, rgba(47, 183, 211, 0) 65%)",
          // Helles Blau oben rechts
          "radial-gradient(55% 70% at 98% -10%, #93c1f7 0%, rgba(147, 193, 247, 0) 60%)",
          // Helles Blau oben links
          "radial-gradient(50% 65% at -6% -10%, #93aef7 0%, rgba(147, 174, 247, 0) 60%)",
          // Violett unten links, läuft in die Bildmitte aus
          "radial-gradient(65% 85% at -8% 120%, #7c3aed 0%, rgba(124, 58, 237, 0) 65%)",
          "radial-gradient(50% 60% at 40% 118%, rgba(109, 74, 232, 0.8) 0%, rgba(109, 74, 232, 0) 60%)",
          "linear-gradient(115deg, #4038cf 0%, #4a43da 50%, #3563d6 100%)",
        ].join(", "),
      }}
    >
          <div className="grid lg:grid-cols-2 items-center">
            {/* Copy */}
            <motion.div
              variants={stagger(0.09)}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="flex flex-col items-center text-center lg:items-start lg:text-left gap-5 p-8 sm:p-12 lg:p-14"
            >
              <motion.span
                variants={fadeUp}
                className="inline-flex items-center rounded-full bg-error px-4 py-1.5 text-sm font-semibold"
              >
                Jetzt Neu
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-[1.15] max-w-lg"
              >
                Der Aufrück-Tracker für das Vergabeverfahren von hochschulstart.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-paper/85 text-lg leading-relaxed max-w-md">
                Tracke live wie du deinem Medizinstudienplatz näher kommst.
              </motion.p>
              <motion.div variants={fadeUp} className="pt-1">
                <Link
                  href="/ratgeber/zulassungsrechner/aufruecken-im-vergabeverfahren/"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-paper text-accent text-base font-semibold min-h-[48px] whitespace-nowrap hover:bg-paper-deep transition-colors duration-200"
                >
                  Mehr erfahren
                  <HoverArrow size={16} />
                </Link>
              </motion.div>
            </motion.div>

            {/* Überlappende Mockups, am unteren Kartenrand beschnitten */}
            <div
              className="relative h-72 sm:h-96 lg:h-[30rem] pointer-events-none select-none"
              aria-hidden="true"
            >
              {/* Hinteres Phone (Analyse) */}
              <motion.div
                initial={mobileEntrance ? { x: "-55%", scale: 0.9 } : false}
                whileInView={mobileEntrance ? { x: 0, scale: 1 } : undefined}
                animate={
                  isDesktop
                    ? hoverActive
                      ? { scale: 1.05, rotate: 4, x: 10 }
                      : { scale: 1, rotate: 0, x: 0 }
                    : undefined
                }
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: mobileEntrance ? 0.8 : 0.4, delay: mobileEntrance ? 0.15 : 0, ease: EASE }}
                className="absolute right-[4%] top-14 sm:top-16 w-[46%] max-w-[15rem] lg:max-w-none will-change-transform"
              >
                <Image
                  src="/images/tracker/aufrueck-tracker-analyse.webp"
                  alt=""
                  width={640}
                  height={1349}
                  className="w-full h-auto drop-shadow-xl"
                />
              </motion.div>
              {/* Vorderes Phone (Dashboard) */}
              <motion.div
                initial={mobileEntrance ? { x: "25%" } : false}
                whileInView={mobileEntrance ? { x: 0 } : undefined}
                animate={
                  isDesktop
                    ? hoverActive
                      ? { scale: 1.05, rotate: -3, x: -8 }
                      : { scale: 1, rotate: 0, x: 0 }
                    : undefined
                }
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: mobileEntrance ? 0.8 : 0.4, delay: mobileEntrance ? 0.15 : 0, ease: EASE }}
                className="absolute right-[36%] top-8 sm:top-10 w-[48%] max-w-[16rem] lg:max-w-none z-10 will-change-transform"
              >
                <Image
                  src="/images/tracker/aufrueck-tracker-dashboard.webp"
                  alt=""
                  width={640}
                  height={1349}
                  className="w-full h-auto drop-shadow-2xl"
                />
              </motion.div>
            </div>
      </div>
    </motion.div>
  );
}
